import { axios } from '@/utils'
import { AuthProviderFactory } from './providers/AuthProviderFactory'
import { AuthProviders, ProviderPriority, SocialProviders } from './constants/authProviders'
import AuthService from './service'
import TokenManager from './utils/tokenManager'
import metric_message from "@/domain/metrics/message"

/**
 * Load user from localStorage on store initialization
 */
const storedUser = TokenManager.loadUser()
if (storedUser) {
    metric_message.setUserId(storedUser.id)
}

/**
 * Load tokens from localStorage on store initialization
 */
const storedAuth = TokenManager.load()

const state = {
    // Auth tokens
    token: storedAuth ? storedAuth.token : null,
    tokenType: storedAuth ? storedAuth.tokenType : null,

    // User data
    user: storedUser,

    // Auth method tracking
    authMethod: null,  // Tracks which provider was used (password, google, etc)

    // Remember flag
    remember: true,

    // Email token flow state
    pendingTokenLogin: null,  // Email waiting for token verification

    // Context parameters from external sources (preview JWT, URL params, etc.)
    // Generic container for any parameter that influences auth behavior
    contextParams: {
        email: null,      // Pre-filled email for login
        picacode: null,   // Checkin code for redirect
        redirect: null,   // Redirect destination
        // Add other params as needed
    }
}

const getters = {
    /**
     * Authentication state
     */
    isAuthenticated: state => !!state.token,
    token: state => state.token,
    tokenType: state => state.tokenType,

    /**
     * User data
     */
    currentUser: state => state.user,
    isAnonymous: state => state.user && state.user.is_anonymous,
    hasAcceptedRequiredPolicies: state => !!(state.user && state.user.accepted_required_policies),

    /**
     * Context parameters from external sources (preview, URL, etc.)
     */
    contextParams: state => state.contextParams,

    /**
     * Pre-filled email from context (backward compatible getter)
     */
    prefilledEmail: state => state.contextParams.email,

    /**
     * Permission checks
     */
    hasPerm: (state) => (perm) => {
        return state.user && state.user.perms && state.user.perms.indexOf(perm) !== -1
    },

    /**
     * Auth provider context (default, without query params)
     * Builds context object for provider isEnabled() decisions
     */
    authProviderContext: (state, getters, rootState, rootGetters) => {
        const factory = AuthProviderFactory.createWithProviders()
        return factory.buildContext(rootState, rootGetters)
    },

    /**
     * Auth provider context with query params
     * Use this getter when you need to check providers based on URL params
     *
     * @param {Object} queryParams - Vue Router query params (this.$route.query)
     * @returns {Object} Context including query params
     */
    authProviderContextWithQuery: (state, getters, rootState, rootGetters) => (queryParams) => {
        const factory = AuthProviderFactory.createWithProviders()
        return factory.buildContext(rootState, rootGetters, queryParams)
    },

    /**
     * Auth provider factory instance
     */
    authProviderFactory: () => {
        return AuthProviderFactory.createWithProviders()
    },

    /**
     * Get list of enabled provider methods (default context, no query params)
     * Each provider decides if it's enabled via isEnabled(context)
     */
    enabledAuthProviders: (state, getters) => {
        const factory = getters.authProviderFactory
        const context = getters.authProviderContext
        return factory.getEnabled(context)
    },

    /**
     * Get list of enabled provider methods with query params
     * Use this to detect JWT/SSO from URL params
     *
     * @param {Object} queryParams - Vue Router query params (this.$route.query)
     * @returns {Array<string>} List of enabled provider methods
     */
    enabledAuthProvidersWithQuery: (state, getters) => (queryParams) => {
        const factory = getters.authProviderFactory
        const context = getters.authProviderContextWithQuery(queryParams)
        return factory.getEnabled(context)
    },

    /**
     * Check if specific provider is enabled (default context)
     */
    isAuthProviderEnabled: (state, getters) => (method) => {
        return getters.enabledAuthProviders.includes(method)
    },

    /**
     * Get primary (default) provider (default context)
     * First enabled provider in priority order
     */
    primaryAuthProvider: (state, getters) => {
        const enabled = getters.enabledAuthProviders
        for (const provider of ProviderPriority) {
            if (enabled.includes(provider)) {
                return provider
            }
        }
        return AuthProviders.PASSWORD // fallback
    },

    /**
     * Get primary provider with query params awareness
     * Use this in auth pages to auto-detect JWT/SSO from URL
     *
     * @param {Object} queryParams - Vue Router query params (this.$route.query)
     * @returns {string} Provider method to use (JWT/SSO if detected, otherwise primary)
     */
    primaryAuthProviderWithQuery: (state, getters) => (queryParams) => {
        const enabled = getters.enabledAuthProvidersWithQuery(queryParams)
        for (const provider of ProviderPriority) {
            if (enabled.includes(provider)) {
                return provider
            }
        }
        return AuthProviders.PASSWORD // fallback
    },

    /**
     * Check if any social login provider is enabled
     */
    hasSocialLoginEnabled: (state, getters) => {
        return getters.enabledAuthProviders.some(p => SocialProviders.includes(p))
    },
}

const mutations = {
    /**
     * Set authentication tokens
     */
    SET_TOKEN(state, { token, tokenType, remember = true }) {
        state.token = token
        state.tokenType = tokenType
        state.remember = remember

        // Update axios headers
        TokenManager.setAxiosHeader(token, tokenType)

        // Persist to localStorage if remember is true
        TokenManager.save(token, tokenType, remember)
    },

    /**
     * Set user data
     */
    SET_USER(state, { user, remember = true }) {
        state.user = user
        metric_message.setUserId(user.id)

        if (remember) {
            TokenManager.saveUser(user, remember)
        }
    },

    /**
     * Set auth method
     */
    SET_AUTH_METHOD(state, method) {
        state.authMethod = method
    },

    /**
     * Set pending email for token login
     */
    SET_PENDING_TOKEN_LOGIN(state, email) {
        state.pendingTokenLogin = email
    },

    /**
     * Clear pending token login
     */
    CLEAR_PENDING_TOKEN_LOGIN(state) {
        state.pendingTokenLogin = null
    },

    /**
     * Set context parameters from external sources
     * Used to pass parameters from preview JWT, URL params, etc.
     * @param {Object} params - Params to merge into contextParams
     * @example
     *   commit('SET_CONTEXT_PARAMS', { email: 'user@example.com', picacode: 'ABC123' })
     */
    SET_CONTEXT_PARAMS(state, params) {
        state.contextParams = { ...state.contextParams, ...params }
    },

    /**
     * Clear specific context parameter
     * @param {string} key - Key to clear (e.g., 'email', 'picacode')
     */
    CLEAR_CONTEXT_PARAM(state, key) {
        state.contextParams[key] = null
    },

    /**
     * Clear all context parameters
     */
    CLEAR_CONTEXT_PARAMS(state) {
        state.contextParams = {
            email: null,
            picacode: null,
            redirect: null
        }
    },

    /**
     * Logout - clear all auth state
     */
    LOGOUT(state) {
        state.token = null
        state.tokenType = null
        state.user = null
        state.authMethod = null
        state.remember = false
        state.pendingTokenLogin = null

        // Clear metrics user ID
        metric_message.setUserId(null)

        // Clear storage
        TokenManager.clear()
        TokenManager.clearAxiosHeader()

        // Clear other app state
        localStorage.removeItem('customModalCheckinDetailShown')
        delete axios.defaults.headers.common['Pica-Event-Owner-Id']

        // Trigger root mutations for cleanup
        this.commit('CLEAR_CUSTOM_MODAL_CHECKIN_DETAIL_SHOWN', null, { root: true })
        this.commit('checkinDetail/clean_popup_viewed', null, { root: true })
    },
}

const actions = {
    /**
     * Universal authenticate action
     * Uses provider pattern to handle any authentication method
     *
     * @param {string} method - Provider method (password, google, email_token, etc)
     * @param {Object} credentials - Provider-specific credentials
     * @param {boolean} remember - Persist tokens to localStorage
     * @returns {Promise<Object>} Auth data with token
     */
    async authenticate({ commit, dispatch }, { method, credentials, remember = true }) {
        const factory = AuthProviderFactory.createWithProviders()
        const provider = factory.getProvider(method)

        if (!provider) {
            throw new Error(`Unknown auth provider: ${method}`)
        }

        try {
            // Provider authenticates and returns {token, tokenType}
            const authData = await provider.authenticate(credentials)

            // Store tokens
            commit('SET_TOKEN', {
                token: authData.token,
                tokenType: authData.tokenType,
                remember
            })

            // Track which method was used
            commit('SET_AUTH_METHOD', method)

            // Clear pending email token if any
            commit('CLEAR_PENDING_TOKEN_LOGIN')

            // Fetch user data
            await dispatch('getCurrentUser', remember)

            return authData
        } catch (error) {
            commit('LOGOUT')
            throw error
        }
    },

    /**
     * Get current user data
     */
    async getCurrentUser({ commit }, remember = true) {
        try {
            const response = await AuthService.getUserData()
            commit('SET_USER', {
                user: response.data,
                remember
            })
            return response
        } catch (error) {
            console.error('Failed to fetch current user:', error)
            throw error
        }
    },

    /**
     * Request login - check if user exists and send token
     * Returns status to determine if user is new or existing
     */
    async requestLogin({ commit }, { email }) {
        const response = await AuthService.requestLogin(email)
        commit('SET_PENDING_TOKEN_LOGIN', email)
        return response.data // { status: "token_sent"|"not_found", email: "..." }
    },

    /**
     * Register new user and send token
     */
    async registerUser({ commit }, payload) {
        const response = await AuthService.registerUser(payload)
        commit('SET_PENDING_TOKEN_LOGIN', payload.email)
        return response.data // { status: "registration_token_sent", email: "..." }
    },

    /**
     * Request magic link - check if user exists and send magic link
     * Returns status to determine if user is new or existing
     */
    async requestMagicLink({ commit }, { email }) {
        const response = await AuthService.requestMagicLink(email)
        commit('SET_PENDING_TOKEN_LOGIN', email)
        return response.data // { status: "magic_link_sent"|"not_found", email: "..." }
    },

    /**
     * Register new user and send magic link
     */
    async registerUserMagicLink({ commit }, payload) {
        const response = await AuthService.registerUserMagicLink(payload)
        commit('SET_PENDING_TOKEN_LOGIN', payload.email)
        return response.data // { status: "registration_magic_link_sent", email: "..." }
    },

    /**
     * Logout
     */
    async logout({ commit, state }) {
        // Provider-specific cleanup (if supported)
        if (state.authMethod) {
            const factory = AuthProviderFactory.createWithProviders()
            const provider = factory.getProvider(state.authMethod)
            if (provider) {
                try {
                    await provider.logout({
                        token: state.token,
                        tokenType: state.tokenType
                    })
                } catch (error) {
                    console.error('Provider logout error:', error)
                }
            }
        }

        commit('LOGOUT')
    },

    /**
     * Restore authentication from localStorage
     * Called on app initialization
     */
    restoreFromStorage({ commit, dispatch }) {
        const authData = TokenManager.load()
        if (!authData) return false

        commit('SET_TOKEN', {
            token: authData.token,
            tokenType: authData.tokenType,
            remember: true
        })

        const user = TokenManager.loadUser()
        if (user) {
            commit('SET_USER', {
                user,
                remember: true
            })
        }

        // Fetch fresh user data in background
        dispatch('getCurrentUser').catch(() => {
            // Token expired, logout
            commit('LOGOUT')
        })

        return true
    },

    // ============================================================
    // LEGACY ACTIONS - Backward Compatibility
    // These wrap the new authenticate() action for existing code
    // ============================================================

    /**
     * @deprecated Use authenticate() instead
     * Legacy password login action
     */
    async login({ dispatch }, user) {
        return dispatch('authenticate', {
            method: AuthProviders.PASSWORD,
            credentials: {
                email: user.email,
                password: user.password
            },
            remember: true
        })
    },

    /**
     * @deprecated Use authenticate() instead
     * Legacy JWT auto-login action
     */
    loginWithJwt({ dispatch }, token) {
        return dispatch('authenticate', {
            method: AuthProviders.JWT,
            credentials: { token },
            remember: true
        })
    },

    /**
     * @deprecated Use authenticate() instead
     * Legacy SSO login action
     */
    async ssoLogin({ dispatch }, { integration_id, token }) {
        return dispatch('authenticate', {
            method: AuthProviders.SSO,
            credentials: { integration_id, token },
            remember: true
        })
    },

    /**
     * @deprecated Use authenticate() instead
     * Legacy social login action
     */
    async socialLogin({ dispatch }, data) {
        // Map backend to provider method
        const backendToMethod = {
            'google-oauth2': AuthProviders.GOOGLE,
            'facebook': AuthProviders.FACEBOOK,
            'apple-id': AuthProviders.APPLE
        }

        const method = backendToMethod[data.backend]
        if (!method) {
            throw new Error(`Unknown social backend: ${data.backend}`)
        }

        return dispatch('authenticate', {
            method,
            credentials: {
                token: data.token,
                allowRegister: data.allow_register || false
            },
            remember: true
        })
    },

    /**
     * @deprecated Use authenticate() instead
     * Legacy IBI login action
     */
    async ibiLogin({ dispatch }, data) {
        return dispatch('authenticate', {
            method: AuthProviders.IBI,
            credentials: data,
            remember: true
        })
    },
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}
